import { useState } from "react";
import { ChevronDown, ZoomIn, Info, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { IMAGE_DESCRIPTIONS } from "@/components/ImageWithZoom";

const heroImageUrl = "https://customer-assets.emergentagent.com/job_75e98cbe-8acd-4806-bc21-99b30d1ba25c/artifacts/xlad8erw_DSC00137.jpg";

export const HeroSection = () => {
  const { t } = useLanguage();
  const [showHeroZoom, setShowHeroZoom] = useState(false);
  const [showHeroInfo, setShowHeroInfo] = useState(false);

  return (
    <>
      <section className="hero-section" data-testid="hero-section" id="main-content" role="banner" aria-label="Welcome to Palmaréca Selah Village">
        <div className="hero-bg">
          <img
            src={heroImageUrl}
            alt="Luxury tropical villa with private pool overlooking palm trees"
            loading="eager"
          />
          <span className="sr-only">{IMAGE_DESCRIPTIONS.hero}</span>
        </div>
        <div className="hero-overlay" aria-hidden="true"></div>
        
        <div className="absolute top-24 right-6 flex gap-2 z-10">
          <button
            onClick={() => setShowHeroZoom(true)}
            className="p-2 bg-black/40 hover:bg-black/60 rounded-full shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Zoom in on hero image"
            title="Zoom in"
          >
            <ZoomIn size={18} className="text-white" />
          </button>
          <button
            onClick={() => setShowHeroInfo(true)}
            className="p-2 bg-black/40 hover:bg-black/60 rounded-full shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="View hero image description"
            title="Image description"
          >
            <Info size={18} className="text-white" />
          </button>
        </div>
        
        <div className="hero-content max-w-4xl mx-auto animate-fade-in-up">
          <Badge className="mb-6 bg-white/20 text-white border-white/30 backdrop-blur-sm text-sm px-4 py-2">
            {t("hero.subtitle")}, Dominican Republic
          </Badge>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            {t("hero.heading1")}<br />
            <span className="italic">{t("hero.heading2")}</span> {t("hero.heading3")}
          </h1>
          <p className="text-white/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto font-light">
            {t("hero.description")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" role="group" aria-label="Main actions">
            <a 
              href="#villas" 
              className="btn-accent pulse-cta inline-flex items-center justify-center gap-2 text-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
              data-testid="hero-book-stay"
              role="button"
            >
              {t("hero.cta")}
              <ChevronDown size={20} aria-hidden="true" />
            </a>
            <a 
              href="#adventures" 
              className="btn-primary bg-white/20 backdrop-blur-sm border-2 border-white/40 hover:bg-white/30 inline-flex items-center justify-center gap-2 text-lg text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
              data-testid="hero-explore-adventures"
              role="button"
            >
              {t("hero.cta2")}
            </a>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce" aria-hidden="true">
          <ChevronDown className="text-white/70" size={32} />
        </div>
      </section>
      
      {showHeroZoom && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setShowHeroZoom(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed hero image"
        >
          <div className="relative max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowHeroZoom(false)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-white rounded-full"
              aria-label="Close zoom view"
            >
              <X size={32} />
            </button>
            <img 
              src={heroImageUrl} 
              alt="Luxury tropical villa - Zoomed view" 
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
            <div className="mt-4 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
              <h3 className="text-white font-semibold mb-2">Palmaréca Selah Village - Luxury Tropical Villa</h3>
              <p className="text-white/80 text-sm leading-relaxed">{IMAGE_DESCRIPTIONS.hero}</p>
            </div>
            <p className="text-center text-white/60 text-sm mt-4">Press Escape or click outside to close</p>
          </div>
        </div>
      )}
      
      {showHeroInfo && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setShowHeroInfo(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="hero-description-title"
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 id="hero-description-title" className="font-serif text-xl font-bold text-primary">Image Description</h3>
              <button
                onClick={() => setShowHeroInfo(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Close description"
              >
                <X size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="flex gap-4 items-start">
              <img 
                src={heroImageUrl} 
                alt="Hero image thumbnail" 
                className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
              />
              <div>
                <h4 className="font-semibold text-foreground mb-2">Luxury Tropical Villa</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{IMAGE_DESCRIPTIONS.hero}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-muted-foreground">
                <strong>Accessibility:</strong> This description helps all visitors understand the image content.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
